import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { ErreurApi, normaliserErreurHttp } from '../../Services/api-error';
import { MaterielCarteComponent, type CarteMateriel } from '../materiel-carte/materiel-carte';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { Installation as InstallationService, type DefautsPrecharges } from '../../Services/installation';
import type { Installation } from '../../Services/EntiteProjet';
import { Authentification } from '../../Services/authentification';
import { MaterielGroupesComponent } from '../materiel-groupes/materiel-groupes';
import { SpinnerOverlayComponent } from '../spinner-overlay/spinner-overlay';
import {
  type DefautsMateriel,
  type EquipementInstallation,
  type TypeDefautMateriel,
  etatMateriel,
  extraireEquipements,
  categorieEquipement,
} from '../../Services/defauts-materiels';

type ChargementDefauts = {
  id: number | null;
  session: number;
  defauts: DefautsMateriel[] | null;
  erreur: string;
};

@Component({
  selector: 'app-installation-equipements-panel',
  imports: [MaterielCarteComponent, MaterielGroupesComponent, SpinnerOverlayComponent],
  templateUrl: './installation-equipements-panel.html',
  styleUrl: './installation-equipements-panel.css',
})
export class InstallationEquipementsPanelComponent {
  readonly installation = input.required<Installation>();
  readonly defautsPrecharges = input<DefautsPrecharges | null>(null);
  readonly affichage = input<'cartes' | 'groupes'>('cartes');
  private readonly service = inject(InstallationService);
  private readonly auth = inject(Authentification);
  private readonly session = toSignal(
    this.auth.sessionChanges.pipe(map(() => this.auth.sessionVersion)),
    {
      initialValue: this.auth.sessionVersion,
    },
  );
  private readonly tentative = signal(0);
  private readonly resultat = signal<ChargementDefauts | null>(null);
  protected readonly courant = computed(() => {
    const resultat = this.resultat();
    return resultat?.id === this.installation().id && resultat.session === this.session()
      ? resultat
      : null;
  });
  protected readonly erreur = computed(() => this.courant()?.erreur ?? '');
  protected readonly chargement = computed(() => !this.erreur() && this.courant()?.defauts == null);
  protected readonly inventaire = computed(() => extraireEquipements(this.installation()));
  protected readonly inventairePartiel = computed(() => {
    const source = this.installation();
    return source.dataloggers === undefined || source.batiments === undefined;
  });
  protected readonly cartes = computed<CarteMateriel[]>(() => {
    const defauts = this.courant()?.defauts;
    const parId = new Map(defauts?.map((m) => [m.idMateriel, m]));
    const equipements = new Map(this.inventaire().map((m) => [m.idMateriel, m]));
    // Conserver les matériels signalés même si l'inventaire ne les contient pas.
    for (const m of defauts ?? []) {
      if (!equipements.has(m.idMateriel)) {
        equipements.set(m.idMateriel, {
          idMateriel: m.idMateriel,
          libelle: m.libelle?.trim() || `Matériel #${m.idMateriel}`,
          type: m.type ?? '',
          marque: '',
          categorie: categorieEquipement(m.type),
        });
      }
    }
    return [...equipements.values()].map((m) => {
      const defautsMateriel = parId.get(m.idMateriel)?.defauts ?? [];
      // Plusieurs entrées API peuvent partager un type : une seule section de menu par type.
      const ticketsParType = new Map<TypeDefautMateriel, Set<number>>();
      for (const { type, tickets } of defautsMateriel) {
        const ids = ticketsParType.get(type) ?? new Set<number>();
        for (const ticket of tickets) ids.add(ticket);
        ticketsParType.set(type, ids);
      }
      return {
        ...m,
        icone: this.iconeEquipement(m),
        defautsActifs: [...ticketsParType.keys()],
        nombreTickets: new Set(defautsMateriel.flatMap((defaut) => defaut.tickets)).size,
        ticketsParDefaut: [...ticketsParType]
          .map(([type, tickets]) => ({
            type,
            libelle: type === 'production' ? 'Production' : 'Communication',
            tickets: [...tickets],
          }))
          .filter((groupe) => groupe.tickets.length > 0),
        etat:
          defauts == null
            ? {
                classe: 'inconnu',
                libelle: this.erreur() ? 'État indisponible' : 'État en cours de chargement',
                icone: 'help',
              }
            : etatMateriel(defautsMateriel),
      };
    });
  });

  protected readonly nombreEnDefaut = computed(
    () =>
      this.cartes().filter(
        (carte) => carte.etat.classe === 'production' || carte.etat.classe === 'communication',
      ).length,
  );

  constructor() {
    effect((onCleanup) => {
      const id = this.installation().id;
      const session = this.session();
      const tentative = this.tentative();
      const precharges = this.defautsPrecharges();
      let annule = false;
      onCleanup(() => {
        annule = true;
      });
      this.resultat.set({ id, session, defauts: null, erreur: '' });
      const promesse = tentative === 0 && precharges?.id === id && precharges.session === session
        ? precharges.promesse
        : this.service.getInstallationDefauts(id);
      void promesse
        .then((defauts) => {
          if (!annule && session === this.auth.sessionVersion) {
            this.resultat.set({ id, session, defauts, erreur: '' });
          }
        })
        .catch((error: unknown) => {
          error = normaliserErreurHttp(error);
          if (annule || session !== this.auth.sessionVersion) return;
          const erreur =
            error instanceof ErreurApi && error.status === 403
              ? 'Vous ne disposez pas des droits pour consulter les défauts de cette installation.'
              : error instanceof ErreurApi && error.status === 404
                ? 'Les équipements sont chargés, mais leurs défauts sont indisponibles (réponse 404 du service).'
                : 'Impossible de charger les défauts des équipements.';
          this.resultat.set({ id, session, defauts: null, erreur });
        });
    });
  }

  protected reessayer() {
    this.tentative.update((n) => n + 1);
  }

  private iconeEquipement(m: EquipementInstallation): string {
    if (m.categorie === 'onduleurs') return 'battery_android_bolt';
    const type = m.type.toLowerCase();
    if (type.includes('datalogger')) return 'lan';
    if (type.includes('capteur') || type.includes('sonde')) return 'light_mode';
    if (type.includes('batterie')) return 'battery_full';
    if (type.includes('compteur')) return 'speed';
    return 'settings_input_component';
  }
}
